// See https://github.com/mayank99/declarative-shadow-dom/blob/main/packages/react/index.tsx
// This is not necessary in React 19
import * as React from 'react';
import * as ReactDOM from 'react-dom';

const isServer = typeof document === 'undefined';
const supportsDSD =
  !isServer && 'shadowRootMode' in HTMLTemplateElement.prototype;

export default function ShadowRoot({
  children,
  dangerouslySetInnerHTML,
  mode = 'open',
}: {
  children?: React.ReactNode;
  mode?: ShadowRootMode;
  dangerouslySetInnerHTML?: {__html: string};
}) {
  const isClient = useIsClient();

  if (isClient) {
    return <ClientShadowRoot mode={mode}>{children}</ClientShadowRoot>;
  }

  // By the time React hydrates, the browser has already removed the template element
  if (supportsDSD) return null;

  if (dangerouslySetInnerHTML) {
    return (
      <template
        {...{shadowrootmode: mode}}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
    );
  }

  // for SSR, use DSD template
  return <template {...{shadowrootmode: mode}}>{children}</template>;
}

function ClientShadowRoot({
  children,
  mode = 'open',
}: {
  children: React.ReactNode;
  mode?: ShadowRootMode;
}) {
  const templateRef = React.useRef<HTMLTemplateElement>(null);
  const [shadowRoot, setShadowRoot] = React.useState<ShadowRoot | null>(null);

  React.useLayoutEffect(() => {
    const parent = templateRef.current?.parentElement;
    if (!parent) return;

    if (parent.shadowRoot) {
      parent.shadowRoot.replaceChildren();
    }

    queueMicrotask(() => {
      ReactDOM.flushSync(() => {
        setShadowRoot(parent.shadowRoot || parent.attachShadow({mode}));
      });
    });

    return () => setShadowRoot(null);
  }, []);

  return shadowRoot ? (
    ReactDOM.createPortal(children, shadowRoot)
  ) : (
    <template ref={templateRef} />
  );
}

const noopSubscribe = () => () => {};

function useIsClient() {
  return React.useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  );
}
